import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, Briefcase, Calendar, Clock, Landmark, MessageSquare, Network, Plane, Trophy, Users } from 'lucide-react';
import { useAuth } from '../../utils/AuthContext';
import { useCanUseTeamChat } from '../../hooks/useCanUseTeamChat';
import { useContractorTimesheetAccess } from '../../hooks/useContractorTimesheetAccess';
import {
  canAccessPeopleBenefitsOrTravel,
  canAccessPeopleHiring,
  canAccessPeopleTeam,
} from '../../utils/employeeBenefitsAccess';

export type PeopleNavVariant = 'employee' | 'fieldStaff' | 'contractor1099';

type Props = {
  variant: PeopleNavVariant;
};

const PeopleNavDropdown: React.FC<Props> = ({ variant }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { canAccess: canContractorTimesheetsNav } = useContractorTimesheetAccess();
  const showTeam = canAccessPeopleTeam(user?.role);
  const showChat = useCanUseTeamChat();
  const showBenefitsAndTravel = canAccessPeopleBenefitsOrTravel(user?.role, user?.designations);
  const showHiring = canAccessPeopleHiring(user?.role, user?.designations);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const isPeopleHubCalendarActive = () => location.pathname === '/people';
  const isPeopleBenefitsActive = () => location.pathname === '/people/benefits';
  const isPeopleHandbookActive = () => location.pathname.startsWith('/people/handbook');
  const isPeopleTravelActive = () =>
    location.pathname === '/people/travel' || location.pathname.startsWith('/people/travel/');
  const isPeopleAchievementsActive = () => location.pathname === '/people/achievements';
  const isPeopleHiringActive = () => location.pathname.startsWith('/people/hiring');
  const isPeopleTeamActive = () => location.pathname.startsWith('/people/team');
  const isPeopleOrgActive = () => location.pathname.startsWith('/people/org');

  const showEmployeeLinks = variant === 'employee';
  const showContractors = canContractorTimesheetsNav;

  return (
    <>
      {showChat && (
        <Link
          to="/team-chat"
          className={`nav-dropdown-item ${isActive('/team-chat') ? 'active' : ''}`}
        >
          <span className="nav-dropdown-icon">
            <MessageSquare size={18} />
          </span>
          Chat
        </Link>
      )}
      {showTeam && (
        <Link
          to="/people/team"
          className={`nav-dropdown-item ${isPeopleTeamActive() ? 'active' : ''}`}
        >
          <span className="nav-dropdown-icon">
            <Users size={18} />
          </span>
          Team
        </Link>
      )}
      {variant !== 'contractor1099' && (
        <Link
          to="/people"
          className={`nav-dropdown-item ${isPeopleHubCalendarActive() ? 'active' : ''}`}
        >
          <span className="nav-dropdown-icon">
            <Calendar size={18} />
          </span>
          Team calendar
        </Link>
      )}
      {showEmployeeLinks && (
        <>
          {showBenefitsAndTravel && (
          <Link
            to="/people/benefits"
            className={`nav-dropdown-item ${isPeopleBenefitsActive() ? 'active' : ''}`}
          >
            <span className="nav-dropdown-icon">
              <Landmark size={18} />
            </span>
            Benefits &amp; payroll
          </Link>
          )}
          <Link
            to="/people/handbook"
            className={`nav-dropdown-item ${isPeopleHandbookActive() ? 'active' : ''}`}
          >
            <span className="nav-dropdown-icon">
              <BookOpen size={18} />
            </span>
            Company handbook
          </Link>
          {showBenefitsAndTravel && (
          <Link
            to="/people/travel"
            className={`nav-dropdown-item ${isPeopleTravelActive() ? 'active' : ''}`}
          >
            <span className="nav-dropdown-icon">
              <Plane size={18} />
            </span>
            Travel
          </Link>
          )}
          {showHiring && (
          <Link
            to="/people/hiring"
            className={`nav-dropdown-item ${isPeopleHiringActive() ? 'active' : ''}`}
          >
            <span className="nav-dropdown-icon">
              <Briefcase size={18} />
            </span>
            Hiring
          </Link>
          )}
          <Link
            to="/people/achievements"
            className={`nav-dropdown-item ${isPeopleAchievementsActive() ? 'active' : ''}`}
          >
            <span className="nav-dropdown-icon">
              <Trophy size={18} />
            </span>
            Certifications &amp; Achievements
          </Link>
          <Link
            to="/people/org"
            className={`nav-dropdown-item ${isPeopleOrgActive() ? 'active' : ''}`}
          >
            <span className="nav-dropdown-icon">
              <Network size={18} />
            </span>
            Org chart
          </Link>
        </>
      )}
      {(variant === 'fieldStaff' || variant === 'contractor1099') && (
        <>
          <Link
            to="/people/handbook"
            className={`nav-dropdown-item ${isPeopleHandbookActive() ? 'active' : ''}`}
          >
            <span className="nav-dropdown-icon">
              <BookOpen size={18} />
            </span>
            Company handbook
          </Link>
          {variant === 'fieldStaff' && (
            <>
              {showBenefitsAndTravel && (
                <>
                  <Link
                    to="/people/benefits"
                    className={`nav-dropdown-item ${isPeopleBenefitsActive() ? 'active' : ''}`}
                  >
                    <span className="nav-dropdown-icon">
                      <Landmark size={18} />
                    </span>
                    Benefits &amp; payroll
                  </Link>
                  <Link
                    to="/people/travel"
                    className={`nav-dropdown-item ${isPeopleTravelActive() ? 'active' : ''}`}
                  >
                    <span className="nav-dropdown-icon">
                      <Plane size={18} />
                    </span>
                    Travel
                  </Link>
                </>
              )}
              <Link
                to="/people/achievements"
                className={`nav-dropdown-item ${isPeopleAchievementsActive() ? 'active' : ''}`}
              >
                <span className="nav-dropdown-icon">
                  <Trophy size={18} />
                </span>
                Certifications &amp; Achievements
              </Link>
              <Link
                to="/people/org"
                className={`nav-dropdown-item ${isPeopleOrgActive() ? 'active' : ''}`}
              >
                <span className="nav-dropdown-icon">
                  <Network size={18} />
                </span>
                Org chart
              </Link>
            </>
          )}
        </>
      )}
      {showContractors && (showEmployeeLinks || variant === 'fieldStaff' || variant === 'contractor1099') && (
        <div className="nav-dropdown-divider" />
      )}
      {showContractors && (
        <Link
          to="/contractor-timesheets"
          className={`nav-dropdown-item ${isActive('/contractor-timesheets') ? 'active' : ''}`}
        >
          <span className="nav-dropdown-icon">
            <Clock size={18} />
          </span>
          Contractors
        </Link>
      )}
    </>
  );
};

export default PeopleNavDropdown;
